import * as Radix from '@radix-ui/react-radio-group'
import { cn } from '../../lib/cn.js'
import { Text } from './Typography.js'

export interface RadioOption {
  value: string
  label: React.ReactNode
  /** Under the label. What choosing this one means. */
  description?: React.ReactNode
  disabled?: boolean
}

/**
 * One of a few, where each needs explaining.
 *
 * 9.6a's *"whether the host is an account or a guest on a link"* is exactly this:
 * two choices that behave completely differently, and a dropdown would hide the
 * difference behind a word. Where the options are self-evident and numerous, that
 * is a `Select`; where there are two or three and each needs a sentence, this.
 */
export function RadioGroup({
  options,
  className,
  ...props
}: React.ComponentPropsWithoutRef<typeof Radix.Root> & { options: RadioOption[] }) {
  return (
    <Radix.Root className={cn('flex flex-col gap-3', className)} {...props}>
      {options.map((o) => (
        <label key={o.value} className="flex cursor-pointer items-start gap-3">
          <Radix.Item
            value={o.value}
            disabled={o.disabled}
            className={cn(
              'mt-0.5 size-5 shrink-0 rounded-full border border-hairline-strong bg-surface outline-none',
              'focus-visible:ring-2 focus-visible:ring-coral-fill/30',
              'data-[state=checked]:border-coral-fill data-[state=checked]:border-[6px]',
              'disabled:opacity-50 disabled:cursor-not-allowed',
            )}
          />
          <span className="flex flex-col gap-0.5">
            <Text as="span" variant="body">
              {o.label}
            </Text>
            {o.description && (
              <Text as="span" variant="caption">
                {o.description}
              </Text>
            )}
          </span>
        </label>
      ))}
    </Radix.Root>
  )
}
